

import { SearchInput } from './components/search-input';
import { quickActions } from './components/quick-actions';
import MoreOptions from './components/more-options';
import {
  Target, Settings, Gamepad2, ImageIcon as LucideImage, ChartSpline, Search
} from 'lucide-react';
import { useState, useEffect, useMemo, memo, useCallback } from 'react';
import './styles/animations.css';
import { useQuery } from '@tanstack/react-query';
import { fetchCampaigns } from '@/src/graphql/spotlight/campaigns';
import { fetchOffers } from '@/src/graphql/spotlight/offers';
import { fetchSpotlightTrafficSources, filterTrafficSources } from '@/src/graphql/spotlight/traffic-sources';
import { fetchSpotlightAffiliateNetworks, filterAffiliateNetworks } from '@/src/graphql/spotlight/affiliate-networks';
import { ITrafficSource } from '@clickster/constant-definitions/dist-esm/entities/traffic-source/types';
import { IAffiliate } from '@clickster/constant-definitions/dist-esm/entities/affiliate/types';
import { CampaignType, SpotlightSearchMode } from './types/template.types';
import { campaignManagementOptions } from './components/campaign-management';
import CampaignRenderBase from './components/campaign-render';
import QuickActionsRenderBase from './components/quick-actions/render';
import CampaignManagementRenderBase from './components/campaign-management/render';
import SearchResults from './components/search-results';
import DefaultSpotlightContent from './components/default-content';

const CampaignRender = memo(CampaignRenderBase);
const QuickActionsRender = memo(QuickActionsRenderBase);
const CampaignManagementRender = memo(CampaignManagementRenderBase);




interface TabItem {
  value: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  className?: string;
}

const tabItems: TabItem[] = [
  { value: 'clickster', label: 'Clickster Settings', icon: Settings },
  { value: 'remote', label: 'Remote Settings', icon: Gamepad2 },
  // { value: 'paths', label: 'Paths', icon: GitFork },
  // { value: 'automations', label: 'Automations', icon: Bot },
  { value: 'ads', label: 'Ads', icon: LucideImage },
  { value: 'placements', label: 'Placements', icon: Target },
  { value: 'reports', label: 'Reports', icon: ChartSpline },
  // { value: 'insights', label: 'Insights', icon: LineChart },
];

interface DashboardSearchProps {
  className?: string;
}

interface Option {
  name: string;
  description: string;
  icon: JSX.Element;
  defaultSettings?: Record<string, any>;
  isNew?: boolean;
  isPopular?: boolean;
}

interface Template {
  name: string;
  icon: JSX.Element;
  description: string;
  preview?: string;
  type: CampaignType;
  overlay?: boolean;
  badges?: {
    text: string;
    color: string;
  }[];
  bidding?: {
    type: string;
    defaultAmount: number;
    minAmount: number;
    strategy: string;
  };
  options: Option[];
}



export default function DashboardSearch({ className }: DashboardSearchProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [debounceKey, setDebounceKey] = useState(0);
  const [isFocused, setIsFocused] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [collapsedNetworks, setCollapsedNetworks] = useState<Record<string, boolean>>({});
  const [campaignFilter, setCampaignFilter] = useState<'active' | 'archived' | 'all'>('active');
  const [searchMode, setSearchMode] = useState<SpotlightSearchMode>('campaigns');
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [offerFilter, setOfferFilter] = useState<'active' | 'archived' | 'all'>('active');
  const [trafficSourceFilter, setTrafficSourceFilter] = useState<'active' | 'inactive' | 'all'>('active');
  const [networkFilter, setNetworkFilter] = useState<'active' | 'inactive' | 'all'>('active');

  useEffect(() => {
    if (!searchQuery) {
      setDebouncedSearchQuery('');
      return;
    }
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery, debounceKey]);

  const createSearchFilter = useCallback(
    (searchQuery: string, statusFilter: string, statusMapping?: Record<string, string>) => ({
      _operators: {
        name: searchQuery ? { regex: `/${searchQuery}/i` } : undefined,
      },
      ...(statusFilter !== 'all'
        ? { status: statusMapping?.[statusFilter] || statusFilter }
        : {})
    }),
    []
  );

  const campaignsFilter = useMemo(
    () => createSearchFilter(debouncedSearchQuery, campaignFilter),
    [createSearchFilter, debouncedSearchQuery, campaignFilter]
  );

  const offersFilter = useMemo(
    () => createSearchFilter(debouncedSearchQuery, offerFilter, {
      'archived': 'inactive'
    }),
    [createSearchFilter, debouncedSearchQuery, offerFilter]
  );

  const { data: sourceCampaignsData = [], isLoading: isFetchingCampaigns } = useQuery([
    'campaigns-gql',
    debouncedSearchQuery,
    campaignFilter,
    searchMode
  ], () => fetchCampaigns(campaignsFilter), {
    enabled: isFocused && debouncedSearchQuery.length > 0 && searchMode === 'campaigns',
    staleTime: 30000,
    keepPreviousData: false
  });

  const { data: sourceOffersData = [], isLoading: isFetchingOffers } = useQuery([
    'offers-gql',
    debouncedSearchQuery,
    offerFilter,
    searchMode
  ], () => fetchOffers(offersFilter), {
    enabled: isFocused && debouncedSearchQuery.length > 0 && searchMode === 'offers',
    staleTime: 30000,
    keepPreviousData: false
  });

  const { data: allTrafficSources = [], isLoading: isFetchingTrafficSources } = useQuery(
    ['spotlight-traffic-sources', searchMode],
    () => fetchSpotlightTrafficSources(),
    {
      enabled: isFocused && searchMode === 'traffic-sources' && debouncedSearchQuery.length > 0,
      staleTime: 60000,
      keepPreviousData: false,
    },
  );

  const { data: allNetworks = [], isLoading: isFetchingNetworks } = useQuery(
    ['spotlight-networks', searchMode],
    () => fetchSpotlightAffiliateNetworks(),
    {
      enabled: isFocused && searchMode === 'networks' && debouncedSearchQuery.length > 0,
      staleTime: 60000,
      keepPreviousData: false,
    },
  );

  const handleClose = useCallback(() => {
    if (!isPinned) {
      setIsFocused(false);
      setShowMoreOptions(false);
      setSearchQuery('');
      setDebouncedSearchQuery('');
      setDebounceKey(0);
      setSearchMode('campaigns');
    }
  }, [isPinned]);

  const handleModeChange = useCallback((mode: SpotlightSearchMode) => {
    setSearchMode(mode);
    setDebouncedSearchQuery('');   // prevent instant request on mode click
    setDebounceKey(k => k + 1);   // restart the 500ms timer with existing searchQuery
  }, []);

  useEffect(() => {
    if (searchMode === 'campaigns') {
      if (debouncedSearchQuery && debouncedSearchQuery.length > 0 && isFetchingCampaigns) {
        setCampaigns(prev => {
          if (prev.length === 0) {
            return prev;
          }
          return [];
        });
      } else if (sourceCampaignsData?.length > 0) {
        // Only update if the data actually changed to prevent infinite loops
        setCampaigns(prev => {
          // If previous is empty and new has data, or if lengths differ, update
          if (prev.length !== sourceCampaignsData.length) {
            return sourceCampaignsData;
          }
          // If same length, check if IDs are different (assuming campaigns have IDs)
          const prevIds = prev.map(c => c._id || c.id).join(',');
          const newIds = sourceCampaignsData.map((c: any) => c._id || c.id).join(',');
          if (prevIds !== newIds) {
            return sourceCampaignsData;
          }
          return prev; // Return previous to avoid unnecessary re-render
        });
      }
    }
  }, [sourceCampaignsData, searchMode, debouncedSearchQuery, isFetchingCampaigns]);


  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && isFocused && !isPinned) {
        handleClose();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isFocused, isPinned, handleClose]);

  // Enhanced keyboard handling for modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isFocused) {
        // ESC to close modal
        if (e.key === 'Escape') {
          e.preventDefault();
          handleClose();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isFocused, handleClose]);

  const handleCampaignFilterClick = () => {
    setCampaignFilter(current => {
      if (current === 'active') return 'archived';
      if (current === 'archived') return 'all';
      return 'active';
    });
  };

  const handleOfferFilterClick = () => {
    setOfferFilter(current => {
      if (current === 'active') return 'archived';
      if (current === 'archived') return 'all';
      return 'active';
    });
  };

  const handleTrafficSourceFilterClick = useCallback(() => {
    setTrafficSourceFilter(current => {
      if (current === 'active') return 'inactive';
      if (current === 'inactive') return 'all';
      return 'active';
    });
  }, []);

  const handleNetworkFilterClick = useCallback(() => {
    setNetworkFilter(current => {
      if (current === 'active') return 'inactive';
      if (current === 'inactive') return 'all';
      return 'active';
    });
  }, []);


  const filterItems = useCallback((items: any[], query: string) => {
    if (!query || !items?.length) return items || [];
    const lowerQuery = query.toLowerCase();

    return items.filter(item =>
      item.text?.toLowerCase().includes(lowerQuery) ||
      item.description?.toLowerCase().includes(lowerQuery)
    ).map(item => ({ ...item }));
  }, []);

  const filteredCampaigns = useMemo(() => {
    if (searchMode !== 'campaigns') return [];
    return campaigns || [];
  }, [searchMode, campaigns]);

  const filteredOffers = useMemo(() => {
    if (searchMode !== 'offers') return [];
    return sourceOffersData || [];
  }, [searchMode, sourceOffersData]);

  const filteredTrafficSources = useMemo((): ITrafficSource[] => {
    if (searchMode !== 'traffic-sources') return [];
    const byQuery = filterTrafficSources(allTrafficSources, debouncedSearchQuery);
    if (trafficSourceFilter === 'all') return byQuery;
    return byQuery.filter((s) => s.status === trafficSourceFilter);
  }, [searchMode, allTrafficSources, debouncedSearchQuery, trafficSourceFilter]);

  const filteredNetworks = useMemo((): IAffiliate[] => {
    if (searchMode !== 'networks') return [];
    const byQuery = filterAffiliateNetworks(allNetworks, debouncedSearchQuery);
    if (networkFilter === 'all') return byQuery;
    return byQuery.filter((n) => (networkFilter === 'active' ? n.status === 'active' : n.status !== 'active'));
  }, [searchMode, allNetworks, debouncedSearchQuery, networkFilter]);

  const isSearching = useMemo(() => {
    return (searchMode === 'campaigns' && isFetchingCampaigns) ||
      (searchMode === 'offers' && isFetchingOffers) ||
      (searchMode === 'traffic-sources' && isFetchingTrafficSources) ||
      (searchMode === 'networks' && isFetchingNetworks);
  }, [searchMode, isFetchingCampaigns, isFetchingOffers, isFetchingTrafficSources, isFetchingNetworks]);

  return (
    <div data-testid="dashboard-spotlight-container" className={className}>
      <SearchInput
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        isFocused={isFocused}
        onFocusChange={setIsFocused}
        onBackdropClick={handleClose}
        showBackdrop={false}
      />

      {isFocused && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-[190] transition-opacity duration-200 ease-out"
            onClick={handleClose}
            data-testid="spotlight-modal-backdrop"
            aria-hidden="true"
          />
          <div
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[200] w-full max-w-2xl max-h-[80vh] h-max-fit rounded-xl bg-white dark:bg-gray-900 shadow-lg border border-gray-200 dark:border-gray-700 transform-gpu"
            style={{
              animation: 'smoothFadeIn 250ms cubic-bezier(0.16, 1, 0.3, 1)',
              willChange: 'transform, opacity',
              contain: 'paint layout'
            }}
            data-testid="spotlight-modal-container">
            <div className="flex flex-col space-y-4 p-4">
              {/* Search Input inside modal */}
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground z-10" />
                <input
                  type="search"
                  placeholder="Find campaigns and resources..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-12 py-2 text-sm font-medium rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none focus:border-transparent focus:ring-2 focus:ring-[#6366f1] focus:ring-opacity-50 focus:shadow-[0_0_10px_rgba(99,102,241,0.3)] dark:focus:[background:linear-gradient(#1f2937,#1f2937)_padding-box,linear-gradient(45deg,#6366f1,#818cf8)_border-box] transition-colors"
                  data-testid="spotlight-modal-search-input"
                  autoFocus
                />
                <kbd className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border 
                             border-gray-200 bg-gray-100 px-1.5  text-[10px] font-medium
                             text-gray-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400">
                  ⌘K
                </kbd>
              </div>

              {searchQuery ? (
                <SearchResults
                  searchQuery={searchQuery}
                  campaigns={filteredCampaigns}
                  campaignFilter={campaignFilter}
                  handleCampaignFilterClick={handleCampaignFilterClick}
                  isFetchingCampaigns={isFetchingCampaigns}
                  tabItems={tabItems}
                  onClose={handleClose}
                  searchMode={searchMode}
                  setSearchMode={handleModeChange}
                  filteredOffers={filteredOffers}
                  offerFilter={offerFilter}
                  handleOfferFilterClick={handleOfferFilterClick}
                  isFetchingOffers={isFetchingOffers}
                  filteredTrafficSources={filteredTrafficSources}
                  isFetchingTrafficSources={isFetchingTrafficSources}
                  trafficSourceFilter={trafficSourceFilter}
                  handleTrafficSourceFilterClick={handleTrafficSourceFilterClick}
                  filteredNetworks={filteredNetworks}
                  isFetchingNetworks={isFetchingNetworks}
                  networkFilter={networkFilter}
                  handleNetworkFilterClick={handleNetworkFilterClick}
                  quickActions={quickActions}
                  campaignManagementOptions={campaignManagementOptions}
                  isSearching={isSearching}
                  filterItems={filterItems}
                />
              ) : showMoreOptions ? (
                <MoreOptions
                  searchQuery={searchQuery}
                  onClose={handleClose}
                  onBack={() => setShowMoreOptions(false)}
                />
              ) : (
                <DefaultSpotlightContent
                  searchQuery={searchQuery}
                  onClose={handleClose}
                  showMoreOptions={showMoreOptions}
                  setShowMoreOptions={setShowMoreOptions}
                  campaignManagementOptions={campaignManagementOptions}
                />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
